import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/services/legal')({
  component: LegalPage,
  head: () => ({
    meta: [
      { title: 'Legal help — Legal Aid, CLCs, courts — Warren-Blackwood' },
      {
        name: 'description',
        content:
          'Plain-English guide to legal help in Warren-Blackwood: Legal Aid WA, Law Society referral, South West Community Legal Centre, SAT, and the Magistrates Court circuit serving the six shires.',
      },
      { property: 'og:title', content: 'Legal help — Warren-Blackwood Electorate Guide' },
      {
        property: 'og:description',
        content:
          'Tenancy, family law, wills, fencing and neighbour disputes — who to call, where the circuit court sits, and how to get free advice.',
      },
    ],
  }),
});

type Phone = { label: string; tel: string; note?: string };
type Block = {
  agency: string;
  what: string;
  phone?: Phone[];
  website?: { label: string; href: string };
  tips?: string[];
};

const SECTIONS: { id: string; title: string; blurb: string; items: Block[] }[] = [
  {
    id: 'free-advice',
    title: 'Free legal advice — start here',
    blurb:
      'If you can\u2019t afford a private lawyer, these are the first places to call. Most offer a free initial conversation and can refer you on if your matter is outside their scope.',
    items: [
      {
        agency: 'Legal Aid WA',
        what: 'Free information and advice; representation in family, criminal and some civil matters if you meet means and merit tests. Telephone advice is available statewide.',
        phone: [
          { label: 'Infoline', tel: '1300650579', note: '1300 650 579' },
          { label: 'Women\u2019s Domestic Violence Helpline 1800 007 339', tel: '1800007339', note: '1800 007 339' },
        ],
        website: { label: 'legalaid.wa.gov.au', href: 'https://www.legalaid.wa.gov.au/' },
        tips: [
          'Have your documents ready before you call \u2014 dates, names, copies of any letters or court papers.',
          'The Bunbury Legal Aid office is the nearest regional office for most of Warren-Blackwood.',
        ],
      },
      {
        agency: 'South West Community Legal Centre (SWCLC)',
        what: 'Bunbury-based community legal centre covering the south-west, including Warren-Blackwood. Free advice clinics, tenancy help, family violence support, and outreach.',
        phone: [
          { label: 'Bunbury office', tel: '0897913206', note: '(08) 9791 3206' },
          { label: 'Freecall', tel: '1800999727', note: '1800 999 727' },
        ],
        website: { label: 'swclc.org.au', href: 'https://www.swclc.org.au/' },
        tips: [
          'SWCLC runs outreach into Manjimup and Margaret River \u2014 ring ahead for the next clinic date.',
        ],
      },
      {
        agency: 'Great Southern Community Legal Services',
        what: 'CLC based in Albany \u2014 the nearest free legal service for Denmark residents.',
        phone: [{ label: 'Albany office', tel: '0898428566', note: '(08) 9842 8566' }],
        website: { label: 'gscls.com.au', href: 'https://www.gscls.com.au/' },
      },
      {
        agency: 'Law Society of WA \u2014 Find a Lawyer',
        what: 'Referral service if you need a private lawyer with a particular specialty. Many firms offer a low-cost initial consultation.',
        phone: [{ label: 'Reception', tel: '0893248600', note: '(08) 9324 8600' }],
        website: { label: 'lawsocietywa.asn.au/find-a-lawyer', href: 'https://www.lawsocietywa.asn.au/find-a-lawyer/' },
      },
    ],
  },
  {
    id: 'tenancy',
    title: 'Tenancy & housing disputes',
    blurb:
      'Private rental disputes \u2014 bond, repairs, rent increases, ending a lease \u2014 go to Consumer Protection in the first instance, then the Magistrates Court for binding orders. Public housing disputes are handled by the Department of Communities.',
    items: [
      {
        agency: 'Consumer Protection \u2014 Tenancy',
        what: 'Free advice on residential tenancy law, bond release, repairs, evictions.',
        phone: [{ label: 'Tenancy line', tel: '1300304054', note: '1300 304 054' }],
        website: { label: 'commerce.wa.gov.au/consumer-protection', href: 'https://www.commerce.wa.gov.au/consumer-protection' },
      },
      {
        agency: 'Circle Green Community Legal \u2014 tenancy advice',
        what: 'Specialist statewide community legal service for tenants. Free advice and assistance.',
        phone: [{ label: 'Advice line', tel: '0861483636', note: '(08) 6148 3636' }],
        website: { label: 'circlegreen.org.au/tenancy', href: 'https://circlegreen.org.au/tenancy/' },
      },
      {
        agency: 'Magistrates Court \u2014 tenancy applications',
        what: 'Binding orders on bond, possession, urgent repairs. Lodged in the Magistrates Court \u2014 see circuit dates below.',
        website: { label: 'magistratescourt.wa.gov.au', href: 'https://www.magistratescourt.wa.gov.au/' },
      },
    ],
  },
  {
    id: 'family',
    title: 'Family law & domestic violence',
    blurb:
      'Family law (separation, parenting, property) is mostly federal. Family violence restraining orders are state and are heard in the Magistrates Court.',
    items: [
      {
        agency: 'Legal Aid WA \u2014 Family Law',
        what: 'Information, advice, dispute resolution and limited representation.',
        phone: [{ label: 'Infoline', tel: '1300650579', note: '1300 650 579' }],
        website: { label: 'legalaid.wa.gov.au/find-legal-answers/family-and-de-facto-relationships', href: 'https://www.legalaid.wa.gov.au/find-legal-answers/family-and-de-facto-relationships' },
      },
      {
        agency: 'Family Violence Restraining Orders (FVRO)',
        what: 'Applications are made at the Magistrates Court. Police can apply on your behalf in urgent situations.',
        website: { label: 'How to apply for an FVRO', href: 'https://www.magistratescourt.wa.gov.au/R/restraining_orders.aspx' },
      },
      {
        agency: '1800RESPECT',
        what: 'National 24/7 family, domestic and sexual violence counselling and information line.',
        phone: [{ label: '24/7', tel: '1800737732', note: '1800 737 732' }],
        website: { label: '1800respect.org.au', href: 'https://www.1800respect.org.au/' },
      },
      {
        agency: 'Women\u2019s Domestic Violence Helpline (WA)',
        what: 'WA state 24/7 helpline with referrals to local refuges.',
        phone: [{ label: '24/7', tel: '1800007339', note: '1800 007 339' }],
      },
      {
        agency: 'Men\u2019s Domestic Violence Helpline (WA)',
        what: 'For men using violence and seeking help to change, and male victims.',
        phone: [{ label: '24/7', tel: '1800000599', note: '1800 000 599' }],
      },
    ],
  },
  {
    id: 'wills',
    title: 'Wills, estates & enduring power of attorney',
    blurb:
      'A current will and an enduring power of attorney avoid almost every avoidable mess after a death or loss of capacity. Most private firms in the region prepare these for a fixed fee; the Public Trustee is the public option.',
    items: [
      {
        agency: 'Public Trustee WA',
        what: 'Wills, enduring powers of attorney, deceased estate administration. Fees apply but are capped and published.',
        phone: [{ label: 'General', tel: '1300746116', note: '1300 746 116' }],
        website: { label: 'publictrustee.wa.gov.au', href: 'https://www.publictrustee.wa.gov.au/' },
      },
      {
        agency: 'Law Society \u2014 find a wills & estates lawyer',
        what: 'Many local firms in Margaret River, Manjimup, Bridgetown and Denmark do wills as fixed-fee work.',
        website: { label: 'lawsocietywa.asn.au/find-a-lawyer', href: 'https://www.lawsocietywa.asn.au/find-a-lawyer/' },
      },
    ],
  },
  {
    id: 'fencing',
    title: 'Dividing fences & neighbour disputes',
    blurb:
      'Dividing fence disputes are governed by the Dividing Fences Act 1961 (WA). Most are resolved by written notice between neighbours; unresolved disputes go to the Magistrates Court.',
    items: [
      {
        agency: 'Citizens Advice Bureau WA \u2014 fencing',
        what: 'Free written guidance and templates for dividing fence notices.',
        phone: [{ label: 'CAB Perth', tel: '0892215711', note: '(08) 9221 5711' }],
        website: { label: 'cabwa.com.au', href: 'https://www.cabwa.com.au/' },
      },
      {
        agency: 'Your local shire',
        what: 'For dangerous structures, encroachments or fencing on a road reserve, the shire is the right first call.',
        website: { label: 'See shire pages', href: '/' },
      },
      {
        agency: 'Magistrates Court \u2014 minor cases',
        what: 'Unresolved dividing fence disputes and minor civil claims up to $10,000 are heard in the Magistrates Court (Minor Cases).',
        website: { label: 'magistratescourt.wa.gov.au', href: 'https://www.magistratescourt.wa.gov.au/' },
      },
    ],
  },
  {
    id: 'sat',
    title: 'State Administrative Tribunal (SAT)',
    blurb:
      'SAT is where you go to challenge a decision of a government body \u2014 most commonly a council planning refusal, but also licensing, guardianship and vocational regulator decisions.',
    items: [
      {
        agency: 'State Administrative Tribunal',
        what: 'Planning appeals (e.g. against a shire refusing a development application), guardianship, professional discipline.',
        phone: [{ label: 'Registry', tel: '1300306017', note: '1300 306 017' }],
        website: { label: 'sat.justice.wa.gov.au', href: 'https://www.sat.justice.wa.gov.au/' },
        tips: [
          'Strict time limits apply \u2014 most planning appeals must be lodged within 28 days of the council\u2019s decision.',
          'You don\u2019t have to be represented by a lawyer, but legal advice early often saves cost later.',
        ],
      },
    ],
  },
  {
    id: 'courts',
    title: 'Magistrates Court \u2014 local circuit',
    blurb:
      'The Magistrates Court travels on circuit through the south-west. There is no court inside every shire \u2014 below is where matters from each shire are usually heard. Always check the current sitting list on the Magistrates Court website before travelling.',
    items: [
      {
        agency: 'Manjimup Magistrates Court',
        what: 'Permanent registry serving Manjimup, Bridgetown-Greenbushes and Boyup Brook shires; Nannup matters are usually listed here. Regular sittings; family violence restraining orders heard.',
        website: { label: 'Court locations', href: 'https://www.magistratescourt.wa.gov.au/M/manjimup_magistrates_court.aspx' },
      },
      {
        agency: 'Margaret River Magistrates Court',
        what: 'Circuit court for the Augusta-Margaret River shire. Sits on scheduled days \u2014 check the list each month.',
        website: { label: 'Court locations', href: 'https://www.magistratescourt.wa.gov.au/M/margaret_river_magistrates_court.aspx' },
      },
      {
        agency: 'Albany Magistrates Court',
        what: 'Permanent court serving the Denmark shire and the wider Great Southern. Family Court matters for the region are also listed here.',
        website: { label: 'Court locations', href: 'https://www.magistratescourt.wa.gov.au/A/albany_magistrates_court.aspx' },
      },
      {
        agency: 'Bunbury Court complex',
        what: 'District Court and higher-level matters from across the south-west, including Warren-Blackwood, are heard in Bunbury.',
        website: { label: 'Court locations', href: 'https://www.magistratescourt.wa.gov.au/B/bunbury_magistrates_court.aspx' },
      },
      {
        agency: 'Children\u2019s Court',
        what: 'Children\u2019s matters are heard at the same circuit courts (Manjimup, Margaret River, Albany, Bunbury) on scheduled days.',
        website: { label: 'childrenscourt.wa.gov.au', href: 'https://www.childrenscourt.wa.gov.au/' },
      },
    ],
  },
];

function LegalPage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.4rem',
    color: 'var(--rivergum)',
    marginTop: 0,
    marginBottom: 'var(--sp-xs)',
    letterSpacing: '0.02em',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.6,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = { color: 'var(--green-mid)', fontWeight: 600 };

  const card: React.CSSProperties = {
    background: 'var(--warm-white, #faf7f0)',
    border: '1px solid var(--light-border)',
    borderRadius: 10,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-md)',
  };
  const agencyBox: React.CSSProperties = {
    background: '#fff',
    border: '1px solid var(--light-border)',
    borderRadius: 8,
    padding: '0.75rem 0.9rem',
    marginTop: '0.6rem',
  };

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 820,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Services &middot; Legal help
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        Legal help
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-lg)',
          lineHeight: 1.5,
        }}
      >
        Tenancy, family law, wills, fencing disputes and neighbour problems are
        the most common legal issues raised at electorate offices. This page is
        a plain-English starting point &mdash; not legal advice. For your
        situation, ring one of the services below.
      </p>

      <div
        style={{
          background: '#eef5fb',
          border: '1px solid #b9d4e8',
          borderRadius: 8,
          padding: 'var(--sp-md)',
          marginBottom: 'var(--sp-lg)',
        }}
      >
        <strong style={{ color: '#1b4f72' }}>Quick contacts</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          Legal Aid WA Infoline &mdash;{' '}
          <a href="tel:1300650579" style={link}>1300 650 579</a>
          <br />
          South West Community Legal Centre (Bunbury) &mdash;{' '}
          <a href="tel:0897913206" style={link}>(08) 9791 3206</a> or freecall{' '}
          <a href="tel:1800999727" style={link}>1800 999 727</a>
          <br />
          Law Society WA &mdash; Find a Lawyer &mdash;{' '}
          <a href="https://www.lawsocietywa.asn.au/find-a-lawyer/" style={link}>
            lawsocietywa.asn.au
          </a>
          <br />
          State Administrative Tribunal &mdash;{' '}
          <a href="tel:1300306017" style={link}>1300 306 017</a>
          <br />
          1800RESPECT (family violence, 24/7) &mdash;{' '}
          <a href="tel:1800737732" style={link}>1800 737 732</a>
        </p>
      </div>

      <nav
        aria-label="Topics on this page"
        style={{
          background: '#eef5fb',
          border: '1px solid #b9d4e8',
          borderRadius: 8,
          padding: 'var(--sp-md)',
          marginBottom: 'var(--sp-lg)',
        }}
      >
        <strong style={{ color: '#1b4f72' }}>Jump to a topic</strong>
        <ul
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '0.4rem 1rem',
            listStyle: 'none',
            padding: 0,
            margin: '0.6rem 0 0',
          }}
        >
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} style={link}>
                {s.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {SECTIONS.map((topic) => (
        <section key={topic.id} id={topic.id} style={card}>
          <h2 style={h2}>{topic.title}</h2>
          <p style={{ ...p, marginBottom: 0 }}>{topic.blurb}</p>

          {topic.items.map((c, i) => (
            <div key={i} style={agencyBox}>
              <div
                style={{
                  fontWeight: 700,
                  color: 'var(--ink)',
                  marginBottom: '0.15rem',
                }}
              >
                {c.agency}
              </div>
              <div
                style={{
                  color: 'var(--ink-mid)',
                  fontSize: '0.95rem',
                  marginBottom: '0.4rem',
                  lineHeight: 1.5,
                }}
              >
                {c.what}
              </div>
              {c.phone && c.phone.length > 0 && (
                <div style={{ fontSize: '0.95rem', marginBottom: '0.25rem' }}>
                  {c.phone.map((ph, j) => (
                    <div key={j}>
                      <span style={{ color: 'var(--ink-mid)' }}>{ph.label}: </span>
                      <a href={`tel:${ph.tel}`} style={link}>
                        {ph.note ?? ph.tel}
                      </a>
                    </div>
                  ))}
                </div>
              )}
              {c.website && (
                <div style={{ fontSize: '0.95rem' }}>
                  <a href={c.website.href} style={link}>
                    {c.website.label} &rarr;
                  </a>
                </div>
              )}
              {c.tips && c.tips.length > 0 && (
                <ul
                  style={{
                    margin: '0.4rem 0 0',
                    paddingLeft: '1.1rem',
                    color: 'var(--ink-mid)',
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                  }}
                >
                  {c.tips.map((tip, k) => (
                    <li key={k}>{tip}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>
      ))}

      <div
        style={{
          background: '#fdf6e3',
          border: '1px solid #e8d9a8',
          borderRadius: 8,
          padding: 'var(--sp-md)',
          marginTop: 'var(--sp-lg)',
        }}
      >
        <strong style={{ color: '#7a5b1e' }}>Not legal advice</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          This page is general information only. For advice on your specific
          situation, contact Legal Aid, a Community Legal Centre or a private
          lawyer. See also{' '}
          <Link to="/services/who-do-i-call" style={link}>
            Who do I call?
          </Link>{' '}
          for the right state agency, or{' '}
          <Link to="/services/federal" style={link}>
            Federal services
          </Link>{' '}
          for Commonwealth matters.
        </p>
      </div>
    </main>
  );
}
